import dayjs from 'dayjs';

export type DayjsValue =
  | string
  | number
  | Date
  | dayjs.Dayjs
  | null
  | undefined;

export function getPlatformTimezoneOffset(timezoneOffset: number) {
  const timezoneOffsetDiff = Math.abs(timezoneOffset - dayjs().utcOffset());
  return timezoneOffsetDiff;
}

export function disablePast(
  timezoneOffset: number,
  value: DayjsValue,
  maxDate?: DayjsValue,
) {
  // Convert value from browser timezone to session timezone
  const valueInSessionTimezone = dayjs(value).utc().utcOffset(timezoneOffset);

  // Use current time in session timezone if no maxDate is provided
  const compareDate = maxDate
    ? dayjs(maxDate).utc().utcOffset(timezoneOffset)
    : dayjs().utc().utcOffset(timezoneOffset);

  return valueInSessionTimezone.isAfter(compareDate, 'minute');
}

export function isAfterDate(value: DayjsValue, maxDate: DayjsValue): boolean {
  return dayjs(value).isAfter(dayjs(maxDate), 'm');
}

export function getPlatformISOString(
  value: DayjsValue,
  timezoneOffset: number,
) {
  const timezoneOffsetDiff = getPlatformTimezoneOffset(timezoneOffset);

  const newDate = dayjs(value)
    .utc()
    .subtract(timezoneOffsetDiff, 'm')
    .toISOString();

  return newDate;
}

export function getOptionalPlatformISOString(
  value?: DayjsValue,
  timezoneOffset?: number, // 0 for timezoneOffset is a valid input
) {
  if (!value || timezoneOffset == null) return undefined;

  return getPlatformISOString(value, timezoneOffset);
}

/**
 *
 * @param date used for date construction [new Date(date); dayjs(date)]. UTC time
 * @param timezoneOffset number in minutes
 * @param daysOffset number in days
 * @description This function is used to fool the Date object to
 * show the correct date in the client timezone
 */
export function adjustDateForClientDisplay(
  date: string,
  timezoneOffset: number,
  daysOffset: number,
): Date {
  const sillyDate = new Date(date);
  // get timezone of the client
  const localTimezoneOffset = sillyDate.getTimezoneOffset();
  // We don't use Date type here since will convert this to local timezone
  const offsetSessionHourDueDate = dayjs(date).add(timezoneOffset, 'minute');
  const offsetIssueTemplateDueDate = offsetSessionHourDueDate.add(
    daysOffset,
    'days',
  );
  // Add local difference to cheat the Date type
  const fooled = offsetIssueTemplateDueDate.add(localTimezoneOffset, 'minute');
  return fooled.toDate();
}
